import { Box, Divider, ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import Icon from 'components/Icon';
import CloseButton from 'components/CloseButton';
import Menu from 'components/Menu/Menu';
import { CroppedText } from 'components/CroppedText';
import { Chip } from 'features/streaming/components/stream-display/StreamBadge';
import { useBreakpoints } from 'hooks/useBreakpoints';
import React, { MouseEvent, useCallback, useState } from 'react';
import { alpha, SxProps, Theme } from '@mui/material/styles';
import { usePiPWindow } from 'components/PiP/PiPProvider';
import { useAppSelector } from 'store/hooks';
import { selectShowAllScreens } from 'features/screenshare/screenshareSlice';

export type ExtraItem = {
  title?: string;
  action?: () => void;
  icon?: string;
  active?: boolean;
  divider?: boolean;
};

type PanelContentSelectorProps<T> = {
  extraItems?: ExtraItem[];
  items: T[];
  activeItem: T | undefined;
  sx?: SxProps;
  renderLabel: (item: T | undefined, active?: boolean) => string;
  onSelect: (idx: number) => void;
  getItemId: (item: T) => string | undefined;
  disabled?: boolean;
  onRemoveItem?: (item: T) => void;
  canRemoveItem?: (item: T) => boolean;
};

const rowHoverVisibilitySx: SxProps<Theme> = {
  '& .CloseButton-root': { visibility: 'hidden' },
  '&:hover .CloseButton-root, &:focus-within .CloseButton-root': { visibility: 'visible' },
};

export const PanelContentSelector = <T,>({
  extraItems = [],
  items,
  activeItem,
  onSelect,
  renderLabel,
  getItemId,
  sx = [],
  disabled = false,
  onRemoveItem,
  canRemoveItem,
}: PanelContentSelectorProps<T>) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  const { isMobile } = useBreakpoints();
  const { pipWindow } = usePiPWindow();
  const showAllScreens = useAppSelector(selectShowAllScreens);

  const isDropDownAvailable = items.length > 1 || extraItems?.length;

  const handleRemoveClick = (event: MouseEvent<HTMLButtonElement>, row: T) => {
    event.stopPropagation();
    onRemoveItem?.(row);
  };

  const canRemoveRow = useCallback(
    (row: T) => Boolean(onRemoveItem) && (canRemoveItem ? canRemoveItem(row) : true),
    [onRemoveItem, canRemoveItem]
  );

  const getRowSx = useCallback(
    (row: T): SxProps<Theme> => {
      const isActive = activeItem && getItemId(activeItem) === getItemId(row);

      const rowBase = canRemoveRow(row)
        ? {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            height: 40,
            ...rowHoverVisibilitySx,
          }
        : {};

      if (isActive) {
        return [
          rowBase,
          (theme: Theme) => ({
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            '&:hover': { backgroundColor: theme.palette.primary.main },
          }),
        ];
      }

      return rowBase;
    },
    [activeItem, getItemId, canRemoveRow]
  );

  const renderExtraMenuItem = useCallback(
    (item: ExtraItem) => {
      if (item.divider) {
        return <Divider key={`divider-${item.title}`} sx={{ my: 1 }} />;
      }

      return (
        <MenuItem
          key={item.title}
          onClick={item.action}
          sx={
            item.active
              ? (theme) => ({
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.main,
                  },
                })
              : null
          }
        >
          {item.icon && (
            <ListItemIcon sx={{ ml: -1 }}>
              <Icon name={item.icon} />
            </ListItemIcon>
          )}
          {item.title && (
            <CroppedText
              title={item.title}
              maxLength={30}
              maxWidth={isMobile ? 200 : 300}
              TextComponent={ListItemText}
            />
          )}
        </MenuItem>
      );
    },
    [isMobile]
  );

  return (
    <Box
      className={!isMobile && !pipWindow && !showAllScreens ? 'hover-child' : ''}
      sx={[
        (theme) => ({
          maxWidth: 'calc(100% - 100px)',
          position: 'absolute',
          top: theme.spacing(2),
          left: '50%',
          width: 'auto',
          transform: 'translateX(-50%)',
          padding: theme.spacing(0, 2),
          zIndex: 1000,
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Menu
        id="file-panel-content-selector"
        open={menuOpen}
        onClose={closeMenu}
        placement="bottom"
        PopperProps={{
          disablePortal: true,
          sx: {
            maxWidth: 'calc(100vw - 100px)',
          },
        }}
        trigger={
          <button
            type="button"
            className="button-appearance-none"
            style={{
              padding: 0,
              cursor: disabled ? 'default' : 'pointer',
              maxWidth: '100%',
            }}
            onClick={() => {
              if (disabled) {
                return;
              }

              if (isDropDownAvailable) {
                setMenuOpen((open) => !open);
              } else {
                setMenuOpen(false);
              }
            }}
          >
            <Chip
              label={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <CroppedText
                    title={renderLabel(activeItem, true)}
                    maxLength={30}
                    maxWidth={isMobile ? 200 : 300}
                  />
                  {isDropDownAvailable && !disabled ? (
                    <Icon
                      name="dropdown-arrow"
                      sx={{
                        flexShrink: 0,
                        fontSize: '32px',
                        marginRight: '-10px',
                        transform: 'translateY(-2px)',
                      }}
                    />
                  ) : null}
                </div>
              }
              sx={(theme) => ({
                ...(!disabled && {
                  '&:hover': {
                    background: alpha(theme.palette.common.black, 0.8),
                  },
                }),
                ...(disabled && {
                  cursor: 'default',
                }),
              })}
            />
          </button>
        }
      >
        <Box sx={{ maxHeight: 'calc(min(285px, 25vh))', overflow: 'auto', maxWidth: '100%' }}>
          {items.map((row, idx) =>
            row ? (
              <MenuItem key={idx} onClick={() => onSelect(idx)} sx={getRowSx(row)}>
                <CroppedText
                  title={renderLabel(row)}
                  maxLength={30}
                  maxWidth={isMobile ? 200 : 300}
                />
                {canRemoveRow(row) ? (
                  <CloseButton
                    onClick={(event) => handleRemoveClick(event, row)}
                    sx={{
                      color: 'inherit',
                      ml: 1,
                      mr: -1,
                    }}
                  />
                ) : null}
              </MenuItem>
            ) : null
          )}
        </Box>
        {extraItems.length > 0 && <Divider sx={{ my: 1 }} />}
        {extraItems.map(renderExtraMenuItem)}
      </Menu>
    </Box>
  );
};
